import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Check,
  Copy,
  Rocket,
  Users,
} from "lucide-react";
import {
  BONUS_TYPE_LABELS,
  COMMISSION_ROUTES,
  COMMISSION_STATUS_LABELS,
  buildReferralLink,
  commissionListResponseSchema,
  formatPrice,
  formatReferralCode,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Badge, Button } from "@/shared/ui/core";
import { KpiCard } from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";
import { useAuth } from "@/features/auth/auth.context";
import { ActivationBadge } from "./dashboard/ActivationBadge";
import {
  useMyDownline,
  useMyNetworkNode,
  useMyOrders,
} from "@/features/mi-negocio/minegocio.api";
import { NetworkCard } from "@/features/mi-negocio/dashboard/cards";
import { OrderStatusBadge } from "@/features/admin/orders/OrderStatusBadge";
import { PanelBanner } from "@/features/panel/PanelBanner";
import { MemberKpis } from "@/features/panel/MemberKpis";
import { OnboardingSteps } from "./OnboardingSteps";
import { InviteQr } from "@/features/network/InviteQr";
import { PackageAutoshipCard } from "./dashboard/PackageAutoshipCard";
import { AvailableCommissionsCard, CommissionClockCard, RankCard } from "./dashboard/SideCards";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El resumen del SOCIO (Anahí, 27-ago): sus métricas de un vistazo —
 * pedidos, comisiones por liberar, invitados—, su enlace de invitación
 * listo para copiar, sus pedidos recientes, y al final SIEMPRE la
 * puerta a la franquicia (para quien aún no la tiene).
 *
 * Composición: KpiCard y los badges del sistema, los mismos endpoints
 * que sus secciones. Ningún número se inventa aquí.
 * ═══════════════════════════════════════════════════════════════════
 */
export function SocioDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const orders = useMyOrders({ page: 1, perPage: 4, role: "buyer" });
  const node = useMyNetworkNode();
  const downline = useMyDownline();
  /* Sus últimas comisiones, para VERLAS sin salir del resumen. */
  const commissions = useQuery({
    queryKey: ["me", "commissions", "recent"],
    queryFn: () =>
      authFetch(`${COMMISSION_ROUTES.mine}?page=1&perPage=5`, commissionListResponseSchema, {
        baseUrl: "",
      }),
  });

  const [copied, setCopied] = useState(false);
  const inviteUrl = node.data
    ? buildReferralLink(node.data.referralCode, window.location.origin)
    : null;

  async function copiar() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // El enlace queda visible para copiarlo a mano.
    }
  }

  const firstName = user?.firstName || user?.fullName || "";

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Resumen")}
        description={t("Hola, {{name}}. Así va tu cuenta.", { name: firstName })}
      />

      {/* ── El banner de campaña, administrado en /admin/banner ───────── */}
      <PanelBanner />

      {/* ── Las métricas ──────────────────────────────────────────────── */}
      <div className="mb-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        {/*
          Los cuatro de cualquier cuenta, en `panel/MemberKpis`: viven
          fuera de esta página para que el resumen del FRANQUICIATARIO
          enseñe exactamente los mismos. Ver el porqué allí.
        */}
        <MemberKpis />
        <KpiCard
          icon={Users}
          item="mi-red"
          label={t("Mi red")}
          value={String(downline.data?.total ?? 0)}
          hint={t("hasta 8 niveles")}
        />
      </div>

      {/* ── Los tres primeros pasos (Anahí, 28-ago) ─────────────────── */}
      <OnboardingSteps />

      {/*
        ── El bloque de producción (Anahí, 28-ago): "Mi paquete · Autoship"
        a la izquierda y, a la derecha, comisiones disponibles, tu nivel
        y el reloj de comisiones. Réplica de `DashboardSocio.tsx` con la
        estética lavanda de la referencia.
      */}
      <div className="mb-4 grid items-start gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <PackageAutoshipCard basePath="/socio" />
        <div className="grid gap-4">
          <AvailableCommissionsCard basePath="/socio" />
          <RankCard basePath="/socio" />
          <CommissionClockCard />
        </div>
      </div>

      {/*
        La columna derecha existe SOLO para la puerta a la franquicia:
        con la franquicia ya adquirida, el contenido cubre TODA la
        pantalla (Anahí, 27-ago) — nada de un carril vacío reservado.
      */}
      <div
        className={
          user?.franchiseeSince
            ? "grid items-start gap-4"
            : "grid items-start gap-4 xl:grid-cols-[1fr_340px]"
        }
      >
        <div className="grid gap-4">
          {/* ── Mi enlace: el motor de todo lo demás ──────────────────── */}
          <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
            <div className="relative grid gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-semibold text-ds-ink">
                  {t("Mi enlace de invitación")}
                </h2>
                {user?.franchiseeSince ? (
                  <Badge variant="indigo" size="sm">{t("Franquiciataria")}</Badge>
                ) : user?.memberSince ? (
                  <Badge variant="info" size="sm">{t("Socio")}</Badge>
                ) : (
                  <Badge variant="neutral" size="sm">{t("Cliente")}</Badge>
                )}
                <ActivationBadge />
              </div>

              {node.isPending ? (
                <p className="text-[13px] text-ds-ink-3">{t("Cargando…")}</p>
              ) : node.data && inviteUrl ? (
                /* La info a la IZQUIERDA, el QR con sus acciones a la
                   DERECHA (Anahí, 27-ago): dos columnas, no una fila
                   revuelta. */
                <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
                  <div className="grid min-w-0 content-start gap-2">
                    <p className="font-mono text-[24px] font-semibold tracking-wider text-ds-ink">
                      {formatReferralCode(node.data.referralCode)}
                    </p>
                    <p className="break-all font-mono text-[12px] text-ds-ink-3">{inviteUrl}</p>
                    <div className="mt-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={copied ? <Check /> : <Copy />}
                        onClick={() => void copiar()}
                      >
                        {copied ? t("Copiado") : t("Copiar enlace")}
                      </Button>
                    </div>
                  </div>
                  <InviteQr
                    url={inviteUrl}
                    fileName="mi-invitacion-its-the-best"
                    shareText={t("Únete a mi equipo en It's The Best:")}
                  />
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="max-w-xl text-[13px] leading-relaxed text-ds-ink-3">
                    {t("Todavía no tienes posición en la red. Actívala y comparte tu enlace: cada compra de tus invitados te genera bonos.")}
                  </p>
                  <Button as={Link} to="/socio/red" rightIcon={<ArrowRight />}>
                    {t("Activar mi red")}
                  </Button>
                </div>
              )}
            </div>
          </section>

          <div className="grid items-start gap-4 xl:grid-cols-2">
          {/* ── Mis comisiones, sin salir del resumen ─────────────────── */}
          <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
            <div className="relative grid gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold text-ds-ink">{t("Mis comisiones")}</h2>
                <Button as={Link} to="/socio/comisiones" variant="ghost" size="sm" rightIcon={<ArrowRight />}>
                  {t("Ver todas")}
                </Button>
              </div>
              {(commissions.data?.items ?? []).length === 0 ? (
                <p className="text-[13px] text-ds-ink-3">
                  {t("Cuando tus invitados compren, tus bonos aparecen aquí.")}
                </p>
              ) : (
                <ul className="grid gap-2">
                  {(commissions.data?.items ?? []).map((commission) => (
                    <li
                      key={commission.id}
                      className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-white px-3.5 py-2.5"
                    >
                      <span className="text-[13px] font-medium text-ds-ink">
                        {BONUS_TYPE_LABELS[commission.bonusType]}
                      </span>
                      <span className="flex-1 text-right font-mono text-[13.5px] font-semibold text-ds-ink">
                        {formatPrice(commission.amountCents, commission.currency)}
                      </span>
                      <Badge
                        variant={commission.status === "paid" ? "success" : "warning"}
                        size="sm"
                        dot
                      >
                        {t(COMMISSION_STATUS_LABELS[commission.status])}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* ── Cómo va mi red ────────────────────────────────────────── */}
          <NetworkCard
            members={downline.data?.items ?? []}
            total={downline.data?.total ?? 0}
            basePath="/socio"
          />
          </div>

          {/* ── Pedidos recientes ─────────────────────────────────────── */}
          <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
            <div className="relative grid gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold text-ds-ink">{t("Pedidos recientes")}</h2>
                <Button as={Link} to="/socio/pedidos" variant="ghost" size="sm" rightIcon={<ArrowRight />}>
                  {t("Ver todos")}
                </Button>
              </div>
              {(orders.data?.items ?? []).length === 0 ? (
                <p className="text-[13px] text-ds-ink-3">
                  {t("Todavía no tienes pedidos. Tu primera compra validada te vuelve socio.")}
                </p>
              ) : (
                <ul className="grid gap-2">
                  {(orders.data?.items ?? []).map((order) => (
                    <li
                      key={order.id}
                      className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-white px-3.5 py-2.5"
                    >
                      <span className="font-mono text-[13px] text-ds-ink-3">{order.folio}</span>
                      <span className="flex-1 text-right font-medium text-ds-ink">
                        {formatPrice(order.totalCents, order.currency)}
                      </span>
                      <OrderStatusBadge status={order.status} />
                      <span className="text-[12px] text-ds-ink-3">
                        {formatOrderDate(order.createdAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* ── SIEMPRE al final: la puerta a la franquicia ─────────────── */}
        {!user?.franchiseeSince && (
          <div className="rounded-3xl bg-ds-ink p-6 text-white shadow-ds-float xl:sticky xl:top-24">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
              <Rocket className="h-5 w-5" />
            </span>
            <h2 className="mt-3 text-[18px] font-semibold leading-snug">
              {t("Adquiere tu franquicia.")}
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-white/75">
              {t("Tu tienda propia dentro de la plataforma, precios de mayorista y los bonos de franquicia del plan.")}
            </p>
            <Button
              as={Link}
              to="/franquicias"
              size="sm"
              rightIcon={<ArrowRight />}
              className="mt-4 bg-white text-ds-ink hover:bg-white/90"
            >
              {t("Conocer la franquicia")}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
